import { Box } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import {
    entityGraphNodeKey,
    EntityGraphApiRisk,
    EntityGraphApiRiskScore,
    EntityGraphNodeDecoration,
    EntityGraphOverlay,
} from "@common/components/entityGraph/EntityGraph.types";
import RiskLevelChip from "../RiskLevelChip";
import { getRiskLevelIndicatorChipStyle, RiskLevelIndicator } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";

type TranslateString = (key: string, interpolation?: { [key: string]: string | number }) => string;

// The risk decorator for the entity-agnostic EntityGraph: the same indicator-driven RiskLevelChip the risk
// overviews use inside the node card, the indicator color as card border, and a "▲N over appetite" badge.
// Entities without an active score keep the graph's neutral styling.
export function buildRiskOverlay(risk: EntityGraphApiRisk, translateString: TranslateString): EntityGraphOverlay {
    const scores = new Map(risk.scores.map((score) => [entityGraphNodeKey(score.type, score.id), score]));
    const indicators = risk.riskLevelIndicators as Array<RiskLevelIndicator>;

    const levelLabel = (level: number) => risk.riskLevels[level - 1] ?? String(level);

    // The scores behind the chip: the consequence and probability of the scenario that produced the headline
    // level, labeled with the risk area's axis names.
    const axesTooltip = (score: EntityGraphApiRiskScore) => {
        if (score.consequence == null || score.probability == null) {
            return null;
        }
        const axisLine = (label: string, levels: Array<string>, value: number) => `${label}: ${levels[value - 1] ?? value} (${value})`;
        return (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box component="span">{axisLine(translateString("consequence"), risk.consequenceLevels, score.consequence)}</Box>
                <Box component="span">{axisLine(translateString("probability"), risk.probabilityLevels, score.probability)}</Box>
            </Box>
        );
    };

    const decorateNode = (node: { key: string }): EntityGraphNodeDecoration | undefined => {
        const score = scores.get(node.key);
        if (!score) {
            return undefined;
        }

        const badge =
            score.overAppetiteCount > 0
                ? {
                      badgeText: `▲${score.overAppetiteCount}`,
                      badgeTooltip: translateString("riskGovernanceOverAppetite"),
                  }
                : {};

        const chipStyle = score.riskLevel != null ? getRiskLevelIndicatorChipStyle(score.riskLevel, indicators) : null;
        if (score.riskLevel == null || !chipStyle) {
            return badge.badgeText ? badge : undefined;
        }

        const chip = <RiskLevelChip level={score.riskLevel} label={levelLabel(score.riskLevel)} color={chipStyle.color} icon={chipStyle.icon} />;
        const tooltip = axesTooltip(score);

        return {
            stroke: chipStyle.color.borderColor,
            chip: tooltip ? (
                <DotLegalTooltip text={tooltip}>
                    <Box component="span" sx={{ display: "inline-flex" }}>
                        {chip}
                    </Box>
                </DotLegalTooltip>
            ) : (
                chip
            ),
            ...badge,
        };
    };

    const renderInspectorExtras = (root: { key: string }) => {
        const overAppetiteCount = scores.get(root.key)?.overAppetiteCount ?? 0;
        if (overAppetiteCount === 0) {
            return null;
        }
        return (
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "0.8rem",
                    fontWeight: theme.typography.fontWeightBold,
                    color: theme.palette.error.main,
                    marginBottom: theme.spacing(2),
                })}
            >
                ▲ {overAppetiteCount} {translateString("riskGovernanceOverAppetite").toLowerCase()}
            </Box>
        );
    };

    return { decorateNode, renderInspectorExtras };
}
